import React, { useState, useEffect } from 'react';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  requireCheckbox?: boolean;
  checkboxLabel?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    title, 
    message, 
    confirmText = "Ja, Prüfung starten", 
    cancelText = "Nein, nur ausblenden",
    requireCheckbox = false,
    checkboxLabel = "Ich habe die Werte geprüft"
}) => {
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (isOpen) setIsChecked(false);
  }, [isOpen]);
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-stone-900/50 dark:bg-black/90 flex items-center justify-center z-[10000] backdrop-blur-md font-sans">
      <div className="bg-pe-card p-8 rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full max-w-md transform transition-all scale-100">
        <h3 className="text-xl font-bold text-pe-accent-text mb-6 text-center border-b border-pe-border dark:border-[#222] pb-4 uppercase tracking-widest">
            {title}
        </h3>
        
        <div className="text-pe-muted dark:text-gray-300 mb-8 text-center leading-relaxed text-sm">
            {message}
        </div>

        {requireCheckbox && (
          <div 
            className="mb-8 flex items-center justify-center gap-4 bg-pe-sunken p-5 rounded-xl border border-pe-border dark:border-[#222] cursor-pointer hover:border-pe-border-strong dark:hover:border-[#444] transition-all shadow-inner"
            onClick={() => setIsChecked(!isChecked)}
          >
            {/* Custom styled checkbox for Noble Slate */}
            <input 
              type="checkbox" 
              checked={isChecked} 
              onChange={(e) => setIsChecked(e.target.checked)} 
              className="w-5 h-5 cursor-pointer appearance-none bg-pe-input dark:bg-black border border-pe-border-strong dark:border-[#444] rounded flex-shrink-0 checked:bg-[#E6C299] checked:border-[#E6C299] relative transition-colors
                         after:content-['✔'] after:absolute after:text-black after:text-sm after:font-black after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:opacity-0 checked:after:opacity-100"
            />
            <label className="text-sm text-pe-muted dark:text-gray-300 cursor-pointer select-none font-bold tracking-wide">
              {checkboxLabel}
            </label>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <button
            onClick={onClose}
            disabled={requireCheckbox && !isChecked}
            className={`flex-1 px-4 py-4 font-bold rounded-xl transition-all text-xs uppercase tracking-widest border
              ${(requireCheckbox && !isChecked) 
                ? 'bg-transparent border-pe-border dark:border-[#222] text-gray-600 cursor-not-allowed' 
                : 'bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300'}`}
          >
            {cancelText}
          </button>

          <button
            onClick={onConfirm}
            disabled={requireCheckbox && !isChecked}
            className={`flex-1 px-4 py-4 font-bold rounded-xl transition-all shadow-lg text-xs uppercase tracking-widest border-none
              ${(requireCheckbox && !isChecked)
                ? 'bg-pe-sunken dark:bg-[#1a1a1a] text-gray-600 cursor-not-allowed'
                : 'bg-pe-accent hover:bg-[#d4b088] dark:hover:bg-white text-black'}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;